import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, LogOut, LayoutDashboard, Package } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const CATS = ["Catene", "Barre", "Ricambi", "Pompe Olio", "Accessori"];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [term, setTerm] = useState(params.get("q") || "");
  const [mobileOpen, setMobileOpen] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    setMobileOpen(false);
    navigate(`/catalogo?q=${encodeURIComponent(term)}`);
  };

  return (
    <header data-testid="main-header" className="sticky top-0 z-50 border-b border-[#e5e5e5] bg-white/85 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <button data-testid="mobile-menu-button" className="lg:hidden p-2" aria-label="Menu">
              <Menu className="w-6 h-6" strokeWidth={1.5} />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="w-80">
            <SheetTitle className="font-display text-xl uppercase">Menu</SheetTitle>
            <nav className="mt-6 flex flex-col gap-1">
              <Link data-testid="mobile-nav-catalogo" to="/catalogo" onClick={() => setMobileOpen(false)} className="py-3 border-b font-semibold uppercase tracking-wide">
                Tutto il catalogo
              </Link>
              {CATS.map((c) => (
                <Link
                  key={c}
                  data-testid={`mobile-nav-${c.toLowerCase().replace(/\s/g, "-")}`}
                  to={`/catalogo?categoria=${encodeURIComponent(c)}`}
                  onClick={() => setMobileOpen(false)}
                  className="py-3 border-b text-muted-foreground"
                >
                  {c}
                </Link>
              ))}
            </nav>
          </SheetContent>
        </Sheet>

        <Link data-testid="logo-link" to="/" className="font-display font-semibold text-xl sm:text-2xl uppercase tracking-tight shrink-0">
          Catena<span className="text-[#ff4500]">Motosega</span>
        </Link>

        <form onSubmit={submitSearch} className="hidden md:flex flex-1 max-w-xl" role="search">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
            <input
              data-testid="header-search-input"
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              placeholder="Cerca catena, barra, ricambio, passo 3/8..."
              className="w-full border-2 border-[#0a0a0a] bg-white pl-10 pr-24 py-2.5 text-sm outline-none focus:border-[#ff4500] transition-colors"
            />
            <button data-testid="header-search-submit" type="submit" className="absolute right-0 top-0 h-full px-5 bg-[#0a0a0a] text-white text-xs font-bold uppercase tracking-wide hover:bg-[#ff4500] transition-colors">
              Cerca
            </button>
          </div>
        </form>

        <div className="ml-auto flex items-center gap-1 sm:gap-2">
          {user && user.role === "admin" && (
            <Link data-testid="nav-admin-link" to="/admin" className="hidden sm:flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wide text-[#062e20] hover:text-[#ff4500] transition-colors">
              <LayoutDashboard className="w-4 h-4" strokeWidth={2} /> Admin
            </Link>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="account-menu-button" className="p-2 hover:text-[#ff4500] transition-colors" aria-label="Account">
                <User className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              {user ? (
                <>
                  <DropdownMenuItem asChild>
                    <Link data-testid="menu-account-link" to="/account" className="cursor-pointer">
                      <Package className="w-4 h-4 mr-2" /> I miei ordini
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem data-testid="menu-logout-button" onClick={logout} className="cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" /> Esci
                  </DropdownMenuItem>
                </>
              ) : (
                <DropdownMenuItem asChild>
                  <Link data-testid="menu-login-link" to="/accedi" className="cursor-pointer">
                    <User className="w-4 h-4 mr-2" /> Accedi / Registrati
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <button data-testid="cart-button" onClick={() => setOpen(true)} className="relative p-2 hover:text-[#ff4500] transition-colors" aria-label="Carrello">
            <ShoppingCart className="w-5 h-5" strokeWidth={1.5} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-0.5 -right-0.5 bg-[#ff4500] text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] min-h-[18px] flex items-center justify-center">
                {count}
              </span>
            )}
          </button>
        </div>
      </div>

      <form onSubmit={submitSearch} className="md:hidden px-4 pb-3" role="search">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={2} />
          <input
            data-testid="header-search-input-mobile"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder="Cerca nel catalogo..."
            className="w-full border-2 border-[#0a0a0a] bg-white pl-10 pr-4 py-2.5 text-sm outline-none focus:border-[#ff4500] transition-colors"
          />
        </div>
      </form>

      <nav className="hidden lg:block border-t border-[#e5e5e5]">
        <div className="max-w-7xl mx-auto px-6 flex gap-8">
          <Link data-testid="nav-catalogo-link" to="/catalogo" className="py-2.5 text-xs font-bold uppercase tracking-[0.15em] hover:text-[#ff4500] transition-colors">
            Tutto il catalogo
          </Link>
          {CATS.map((c) => (
            <Link
              key={c}
              data-testid={`nav-cat-${c.toLowerCase().replace(/\s/g, "-")}`}
              to={`/catalogo?categoria=${encodeURIComponent(c)}`}
              className="py-2.5 text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground hover:text-[#ff4500] transition-colors"
            >
              {c}
            </Link>
          ))}
        </div>
      </nav>
    </header>
  );
}
